import { sanitizeOrganizationSwitchRedirect } from "./organizationSwitchUrl";

export type OrganizationListener = (orgId: number | null) => void;
export type GetTokenFn = () => Promise<string | undefined>;
type FetchDefaultOrgFromMeFn = () => Promise<number | null>;

type OrganizationConfig = {
  getToken: GetTokenFn;
  organizationApiUrl?: string;
  switchOrgPath?: string;
  urlOrgQueryParam?: string;
  fetchDefaultOrgFromMe?: FetchDefaultOrgFromMeFn;
};

let organizationId: number | null = null;
let listeners: OrganizationListener[] = [];
let getTokenFn: GetTokenFn | null = null;
let organizationApiUrl: string | null = null;
let switchOrgPath = "/switch-org";
let urlOrgQueryParam = "orgId";
let fetchDefaultOrgFromMeFn: FetchDefaultOrgFromMeFn | null = null;

function orgIdFromUrl(): number | null {
  if (typeof window === "undefined") return null;
  const pathMatch = window.location.pathname.match(/^\/orgs\/(\d+)(\/|$)/);
  if (pathMatch) {
    const value = Number(pathMatch[1]);
    if (Number.isFinite(value)) return value;
  }
  const raw = new URLSearchParams(window.location.search).get(urlOrgQueryParam);
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export function configureOrganization(options: OrganizationConfig) {
  getTokenFn = options.getToken;
  if (options.organizationApiUrl != null) {
    organizationApiUrl = options.organizationApiUrl.endsWith("/")
      ? options.organizationApiUrl.slice(0, -1)
      : options.organizationApiUrl;
  }
  if (options.switchOrgPath) {
    switchOrgPath = options.switchOrgPath;
  }
  if (options.urlOrgQueryParam) {
    urlOrgQueryParam = options.urlOrgQueryParam;
  }
  if (options.fetchDefaultOrgFromMe) {
    fetchDefaultOrgFromMeFn = options.fetchDefaultOrgFromMe;
  }
}

export function getCurrentOrganizationId(): number | null {
  return organizationId;
}

export function setCurrentOrganizationId(id: number | null) {
  const oldId = organizationId;
  organizationId = id;
  if (oldId === id) return;
  listeners.forEach((listener) => {
    try {
      listener(id);
    } catch (error) {
      console.error("Error in organization listener:", error);
    }
  });
}

export function clearCurrentOrganization() {
  setCurrentOrganizationId(null);
}

export function addOrganizationListener(listener: OrganizationListener): () => void {
  listeners.push(listener);
  return () => {
    listeners = listeners.filter((candidate) => candidate !== listener);
  };
}

export async function resolveEffectiveOrganizationId(): Promise<number | null> {
  const fromUrl = orgIdFromUrl();
  if (fromUrl !== null) return fromUrl;
  if (organizationId !== null) return organizationId;
  if (fetchDefaultOrgFromMeFn) {
    return await fetchDefaultOrgFromMeFn();
  }
  return null;
}

export async function persistRecentOrganization(orgId: number): Promise<void> {
  if (!getTokenFn || !organizationApiUrl) {
    return;
  }
  try {
    const token = await getTokenFn();
    if (!token) return;
    const response = await fetch(`${organizationApiUrl}/user/me/recent-organization/`, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ organization_id: orgId }),
    });
    if (!response.ok) {
      console.error("Failed to persist MRU organization:", response.status);
    }
  } catch (error) {
    console.error("Error persisting MRU organization:", error);
  }
}

export function startOrganizationSwitch(orgId: number, redirectUrl: string, meta?: { orgName?: string | null }): void {
  const switchUrl = new URL(switchOrgPath, window.location.origin);
  switchUrl.searchParams.set("orgId", String(orgId));
  switchUrl.searchParams.set("redirectTo", sanitizeOrganizationSwitchRedirect(redirectUrl, "/"));
  if (meta?.orgName) {
    switchUrl.searchParams.set("orgName", meta.orgName);
  }
  window.location.replace(switchUrl.toString());
}
